<template>
  <DeleteConfirmDialog
    v-if="documentToDelete"
    :visible="deleteDialogVisiblity"
    :title="i18n.t('views.applicationDetails.documentsCard.deleteDocument')"
    :sub-title="documentDeleteTitle"
    :loading="loadingDelete"
    @cancel="deleteDialogVisiblity = false"
    @delete="handleDelete(documentToDelete)" />

  <DocumentUploadDialog
    v-model:show-drawer="showUploadDrawer.show"
    v-model:existing-document="showUploadDrawer.existingDocument"
    v-model:edit-mode="showUploadDrawer.editMode"
    :application-id="props.applicationId"
    @upload-document-success="
      () => {
        showUploadDrawer.editMode = false
        showUploadDrawer.existingDocument = null
        getDocumentList()
      }
    "
    @download-document="(document: DocumentInfo) => getDocument(document)" />

  <SmTable
    v-if="loading || documentList.length"
    ref="table"
    :data="filteredDocumentList"
    key-field="_id"
    :columns="columns"
    :loading="loading"
    :selectable="true">
    <template #application="{ row }">
      <div
        v-if="row.software_id && getApplication(row.software_id)"
        class="flex items-center gap-3">
        <ApplicationIcon
          :application-name="
            getApplication(row.software_id)?.software_name ||
            getApplication(row.software_id)?.software_name ||
            ''
          "
          :application-type="getApplication(row.software_id)?.type" />
        <p>
          {{
            getApplication(row.software_id)?.display_name ||
            getApplication(row.software_id)?.software_name
          }}
        </p>
      </div>
      <div v-else>
        <p class="no-data">{{ i18n.t('noSoftwareConnected') }}</p>
      </div>
    </template>
    <template #name="{ row }">
      <TextMultiline
        :text="row.title ?? undefined"
        :lines-to-show="1"
        :show-tool-tip="true"
        tooltip-max-width="250px"
        tooltip-placement="right" />
    </template>
    <template #size="{ row }">
      <p>{{ `${(row.file_size / 1000).toFixed(2)} KB` }}</p>
    </template>
    <template #date="{ row }">
      <p>{{ displayDate(dayjs(row.date).toDate()) }}</p>
    </template>
    <template #action="{ row }">
      <SmDropdown trigger="click">
        <SmDropdownItem @click="getDocument(row)">
          <div class="flex items-center gap-3">
            <ArrowDownToLine :size="18" />
            {{ i18n.t('views.documents.table.tooltips.download') }}
          </div>
        </SmDropdownItem>
        <SmDropdownItem @click="handleEdit(row)">
          <div class="flex items-center gap-3">
            <PencilLine :size="18" />
            {{ i18n.t('views.documents.table.tooltips.edit') }}
          </div>
        </SmDropdownItem>
        <SmDropdownItem @click="showDeleteDialog([row])">
          <div class="flex items-center gap-3">
            <Trash :size="18" />
            {{ i18n.t('general.delete') }}
          </div>
        </SmDropdownItem>
      </SmDropdown>
    </template>

    <template #footer-application> </template>

    <template #footer>
      <div
        v-if="selection && selection?.length > 0"
        class="flex w-full items-center gap-3">
        <p class="font-medium text-primary">
          {{ `${i18n.t('xDocuments', { count: selection?.length })}` }}
        </p>
        <SmButton
          size="small"
          class="ml-auto"
          type="primary"
          @click="downloadDocumentList()">
          {{
            i18n.t('views.documents.selectButton.downloadButtonText', {
              count: selection.length,
            })
          }}
        </SmButton>
        <SmButton
          size="small"
          class="ml-auto"
          type="danger"
          @click="showDeleteDialog(selection)">
          {{
            i18n.t('views.documents.selectButton.deleteButtonText', {
              count: selection.length,
            })
          }}
        </SmButton>
      </div>
      <p v-else>
        {{ i18n.t('xDocuments', { count: filteredDocumentList.length }) }}
      </p>
    </template>
  </SmTable>

  <EmptyState
    v-else-if="!loading && documentList.length === 0"
    :icon="Folders"
    :title="i18n.t('documentsEmptyStateTitle')"
    :text="i18n.t('documentsEmptyStateText')"
    :create-label="i18n.t('views.documents.addDocument')"
    :show-docs-btn="false"
    class="empty-state"
    @create="emit('addDocument')" />
</template>

<script setup lang="ts">
  import dayjs from 'dayjs'
  import { useI18n } from 'vue-i18n'
  import JSZip from 'jszip'
  import { DocumentInfo, DocumentsService, SoftwareOut } from '@/client'
  import DocumentUploadDialog from '@/components/documents/DocumentUploadDialog.vue'

  import { ApplicationStore } from '@/stores/application/application.store'
  import { displayDate } from '@/utils/formatter/date'
  import { Column } from '../sm/SmTable.types'
  import { capitalize } from '@/utils/formatter/formatter'
  import { ArrowDownToLine, Folders, PencilLine, Trash } from 'lucide-vue-next'
  import { sendToast } from '../sm/SmNotification'
  import { saveAs } from 'file-saver'
  import { ComponentExposed } from 'vue-component-type-helpers'
  import SmTable from '@/components/sm/SmTable.vue'
  import { downloadResource } from '@/utils/download'

  const i18n = useI18n()
  const documentList = ref<DocumentInfo[]>([])
  const table = ref<ComponentExposed<typeof SmTable<DocumentInfo>>>()

  // Define Props
  const props = withDefaults(
    defineProps<{
      applicationId?: string
      smallCardMode: boolean
      searchQuery?: string
      viewMode?: boolean
      selectedDateRange?: [string, string]
    }>(),
    {
      applicationId: undefined,
      smallCardMode: false,
      searchQuery: '',
      viewMode: false,
      selectedDateRange: undefined,
    }
  )

  // Define Emits
  const emit = defineEmits([
    'loadDocumentList',
    'documentListLoaded',
    'documentList',
    'selectedDocumentCountChanged',
    'addDocument',
  ])

  const allApplications = ApplicationStore.useApplications<SoftwareOut>().data
  const loading = ref(false)
  const searchQuery = computed(() => props.searchQuery)
  const selectedDateRange = computed(() => props.selectedDateRange)

  const filteredDocumentList = computed(() =>
    documentList.value
      .filter((document) => {
        // Filter by Date Range
        if (selectedDateRange.value) {
          if (!document.date) return false
          const [start, end] = selectedDateRange.value
          return dayjs(document.date).isBetween(
            dayjs(start),
            dayjs(end).endOf('day'),
            'day',
            '[]'
          )
        }
        return true
      })
      .filter((document) => {
        return (
          (!props.applicationId ||
            document.software_id === props.applicationId) &&
          (!searchQuery.value ||
            (document.software_id &&
              getApplication(document.software_id)
                ?.software_name.toLowerCase()
                .includes(searchQuery.value.toLowerCase())) ||
            (document.title &&
              document.title
                .toLowerCase()
                .includes(searchQuery.value.toLowerCase())) ||
            dayjs(document.date)
              .format('DD.MM.YYYY')
              .includes(searchQuery.value.toLowerCase()))
        )
      })
  )

  const columns = computed<Column<DocumentInfo>[]>(() => {
    const baseColumns: Column<DocumentInfo>[] = [
      {
        key: 'name',
        label: i18n.t('name'),
        sortable: true,
        width: 3,
      },
      {
        key: 'size',
        label: i18n.t('views.documents.table.fileSize'),
        sortable: false,
        width: 2,
      },
      {
        key: 'date',
        label: i18n.t('views.documents.table.date'),
        sortable: false,
        width: 2,
      },
      {
        key: 'action',
        label: '',
        sortable: false,
        width: '50px',
      },
    ]

    if (!props.applicationId) {
      baseColumns.unshift({
        key: 'application',
        label: capitalize(i18n.t('application')),
        sortable: true,
        width: 2,
      })
    }

    return baseColumns
  })

  // -------------------------------

  interface ShowUploadDrawer {
    show: boolean
    editMode: boolean
    existingDocument: DocumentInfo | null
  }

  const showUploadDrawer = ref<ShowUploadDrawer>({
    show: false,
    editMode: false,
    existingDocument: null,
  })

  /**
   * Opens a drawer:
   * @param editMode If true, the drawer will be opened in edit mode
   * @param existingDocument If editMode is true, this will be the document that will be edited
   */
  function openDrawer(
    editMode: boolean,
    existingDocument: DocumentInfo | null
  ) {
    showUploadDrawer.value.editMode = editMode
    showUploadDrawer.value.existingDocument = existingDocument
    showUploadDrawer.value.show = true
  }

  /**
   * Opens the Edit Drawer or the Upload Drawer
   * @param index The index of the document in the list
   * @param row The document to edit
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  function handleEdit(row: DocumentInfo) {
    openDrawer(true, row)
  }

  /**
   * Updates the document list. All documents for this software are fetched from the server.
   */
  function getDocumentList() {
    emit('loadDocumentList')
    loading.value = true
    DocumentsService.getDocumentInfoListApiV1SoftwareDocumentsGet({}).then(
      (data) => {
        documentList.value = data
        loading.value = false
        emit('documentListLoaded', data)
      }
    )
  }

  function getApplication(id: string) {
    return allApplications.value.find((application) => application._id === id)
  }

  /**
   * Gets the document from the server
   * @param row The document
   */
  function getDocument(rowDocument: DocumentInfo) {
    downloadResource({ documentId: rowDocument._id }).then((blob) => {
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      Object.assign(a, {
        href: url,
        download: rowDocument.filename,
        target: '_blank',
      })
      a.style.display = 'none'
      document.body.appendChild(a)
      a.click()
      window.URL.revokeObjectURL(url)
    })
  }

  /**
   * Delete a document
   * @param index The index of the document in the list
   * @param row The document to delete
   */
  const documentToDelete = ref<DocumentInfo[]>()
  const deleteDialogVisiblity = ref<boolean>(false)
  const loadingDelete = ref<boolean>(false)

  const documentDeleteTitle = computed(() => {
    if (documentToDelete.value?.length) {
      const title =
        documentToDelete.value?.length === 1
          ? documentToDelete.value?.[0]?.title ||
            documentToDelete.value?.[0]?.filename
          : i18n.t('xDocuments', {
              count: documentToDelete.value?.length ?? 0,
            })

      return i18n.t('views.documents.table.tooltips.delete', {
        title,
      })
    }
  })

  function showDeleteDialog(row: DocumentInfo[]) {
    // Store a copy so later selection changes don't mutate the pending list
    documentToDelete.value = [...row]
    deleteDialogVisiblity.value = true
  }

  watch(
    () => deleteDialogVisiblity.value,
    (newValue) => {
      if (!newValue) {
        loadingDelete.value = false
        documentToDelete.value = undefined
      }
    }
  )
  function handleDelete(document: DocumentInfo[]) {
    if (document.length > 1) {
      deleteDocuments(document)
      return
    }

    loadingDelete.value = true
    DocumentsService.deleteDocumentApiV1SoftwareDocumentsDocumentIdDelete({
      documentId: document[0]._id as string,
    }).then(function (response) {
      getDocumentList()
      sendToast(
        i18n.t('notifications.documentTable.deleteSuccess.title'),
        i18n.t('notifications.documentTable.deleteSuccess.message'),
        'success'
      )
      loadingDelete.value = false
      deleteDialogVisiblity.value = false
      documentToDelete.value = undefined
      return response.text()
    })

    table.value?.resetSelection()
  }

  function deleteDocuments(
    documents: DocumentInfo[] = documentToDelete.value ?? []
  ) {
    const deletePromises = documents.map((document) =>
      DocumentsService.deleteDocumentApiV1SoftwareDocumentsDocumentIdDelete({
        documentId: document._id,
      })
    )
    Promise.all(deletePromises)
      .then(() => {
        sendToast(
          i18n.t('notifications.documentTable.bulkDeleteSuccess.title'),
          i18n.t('notifications.documentTable.bulkDeleteSuccess.message'),
          'success'
        )
        getDocumentList()
      })
      .catch(() => {
        sendToast(
          i18n.t('notifications.documentTable.deleteFailed.title'),
          i18n.t('notifications.documentTable.deleteFailed.message'),
          'error'
        )
        getDocumentList()
      })

    loadingDelete.value = false
    deleteDialogVisiblity.value = false
    documentToDelete.value = undefined
    table.value?.resetSelection()
  }

  const selection = computed(() => {
    return table.value?.selection
  })

  watch(
    () => selection.value,
    () => {
      emit('selectedDocumentCountChanged', selection.value)
    }
  )

  const downloadDocumentList = async () => {
    sendToast(
      i18n.t('notifications.documentTable.downloadStarted.title'),
      i18n.t('notifications.documentTable.downloadStarted.message'),
      'info'
    )
    const zip = new JSZip()

    const downloads = (selection.value ?? []).map(async (document) => {
      const blob = await downloadResource({ documentId: document._id })
      zip.file(
        `${document.software_id}_${generateRandomId(5)}_${document.filename}`,
        blob
      )
    })

    try {
      await Promise.all(downloads)
      const blob = await zip.generateAsync({ type: 'blob' })
      saveAs(blob, 'Dokumente.zip')
      sendToast(
        i18n.t('notifications.documentTable.downloadSuccess.title'),
        i18n.t('notifications.documentTable.downloadSuccess.message'),
        'success'
      )
    } catch (error) {
      sendToast(
        i18n.t('notifications.documentTable.downloadFailed.title'),
        i18n.t('notifications.documentTable.downloadFailed.message'),
        'error'
      )
    }
  }

  const generateRandomId = (length: number): string => {
    let result = ''
    const characters =
      'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789'
    const charactersLength = characters.length
    for (let i = 0; i < length; i++) {
      result += characters.charAt(Math.floor(Math.random() * charactersLength))
    }
    return result
  }

  getDocumentList()

  defineExpose({
    getDocumentList,
    openDrawer,
    downloadDocumentList,
    deleteDocuments,
  })
</script>

<style scoped lang="scss">
  .company-mini-img {
    width: 15px;
    height: 15px;
    flex-shrink: 0;
  }
</style>
